import { EAnalyticsEvent } from "@frontend-shared/product-analytics";
import { useCallback, useEffect, useRef } from "react";
import type { CallBackProps } from "react-joyride";

import { useProductAnalytics } from "@/hooks/use-product-analytics";
import type { IUseTourStepViewedProps } from "@/providers/types";
import { resolveJoyrideStepView } from "@/providers/utils";

// NOTE: NSAI-5334 — the one place `onboarding_tour_step_viewed` is emitted on
// web. It listens to Joyride's TOOLTIP event, which fires when a step's
// tooltip is actually on screen, rather than to `setCurrentStep`, which is
// called from navigation, the missing-target correction effect and the
// pause/resume retry. TOOLTIP still repeats for one step (the correction
// effect re-points the index, a paused step resumes), so
// `resolveJoyrideStepView` dedups on the step's target — the same shared rule
// mobile uses.
export const useTourStepViewed = ({
  tour,
  isRunning,
}: IUseTourStepViewedProps): ((data: CallBackProps) => void) => {
  const { capture } = useProductAnalytics();
  const lastViewedStepKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isRunning) {
      lastViewedStepKeyRef.current = null;
    }
  }, [isRunning]);

  return useCallback(
    (data: CallBackProps): void => {
      const { shouldCapture, lastViewedStepKey } = resolveJoyrideStepView({
        data,
        lastViewedStepKey: lastViewedStepKeyRef.current,
      });
      lastViewedStepKeyRef.current = lastViewedStepKey;

      if (!shouldCapture) return;

      capture({
        event: EAnalyticsEvent.ONBOARDING_TOUR_STEP_VIEWED,
        properties: { tour, step_index: data.index },
      });
    },
    [capture, tour],
  );
};
